// main tools
import { captureMessage } from '@sentry/nextjs'
import axios from 'axios'

// components
import { Typography } from '@centria-ui/components/atoms/typography'

// hooks
import { useApp } from '@centria-ui/hooks/use-app'

// utils
import { formatGraphqlError, formatErrorEnums } from './format-errors'

// enums
import { variantsEnum } from '@centria-ui/commons/enums'

// types
import type { microServicesEnum } from '@dr-keyword-commons/enums'
import type { ApolloError } from '@apollo/client'

import { useTranslation } from 'react-i18next'
import { getErrorMessage } from '@dr-keyword-utils/get-error-message'

type FormattedErrorDataType = {
  enum: string
  errors?: string[]
  ms?: keyof typeof microServicesEnum
}

export class BaseService {
  private static alertDetail = (data: {
    detail: string
    errors?: string[]
    errorCode?: string
    fontColor: keyof typeof variantsEnum
  }) => (
    <>
      <Typography Tag='h4' fontcolor={data.fontColor}>
        {data.detail}
      </Typography>
      {data.errors && (
        <Typography Tag='ul' fontcolor={data.fontColor}>
          {data.errors.map((error, index) => (
            <Typography Tag='li' key={index}>
              {error}
            </Typography>
          ))}
        </Typography>
      )}
      {data.errorCode && (
        <Typography
          Tag='p'
          className='mt-2'
          fontSize='minimum'
          fontcolor={data.fontColor}>
          {data.errorCode}
        </Typography>
      )}
    </>
  )

  protected static sendMessageToSentry = (message: string) =>
    captureMessage(message)

  protected static formatGraphqlError = (error: ApolloError) =>
    formatGraphqlError(error)

  public static getBaseService = () => {
    const { toast } = useApp()
    const { t } = useTranslation()

    const showSuccess = (
      detail = 'La acción se ha realizado con éxito',
      errors?: string[]
    ) =>
      toast()?.show({
        life: 5000,
        summary: 'Éxito',
        severity: 'success',
        detail: this.alertDetail({ detail, fontColor: 'success', errors })
      })

    const showError = (
      detail = 'Ha ocurrido un error inesperado',
      errors?: string[]
    ) =>
      toast()?.show({
        life: 5000,
        summary: 'Error',
        severity: 'error',
        detail: this.alertDetail({ detail, fontColor: 'danger', errors })
      })

    const showWarning = (
      detail = 'Está a punto de realizar una acción que no se puede deshacer',
      errors?: string[]
    ) =>
      toast()?.show({
        life: 7500,
        severity: 'warn',
        summary: 'Cuidado',
        detail: this.alertDetail({ detail, fontColor: 'warning', errors })
      })

    const showInfo = (
      detail = 'Está a punto de realizar una acción que no se puede deshacer',
      errors?: string[]
    ) =>
      toast()?.show({
        life: 5000,
        severity: 'info',
        summary: 'Información',
        detail: this.alertDetail({ detail, fontColor: 'info', errors })
      })

    const showFormattedError = (
      data?: FormattedErrorDataType,
      timetoLife?: number
    ) => {
      const detailText = getErrorMessage(data.enum, t)

      toast()?.show({
        summary: 'Error',
        severity: 'error',
        life: timetoLife ?? 5000,
        detail: this.alertDetail({
          detail: detailText,
          errors: data.errors,
          fontColor: 'danger',
          errorCode: data.enum
        })
      })

      if (data.errors) console.error(data.errors)
    }

    const verifyExternalUrl = async (url: string) => {
      const { status } = await axios
        .post('/api/verify-external-url', { url })
        .then((response) => response)
        .catch((error) => error)
      return status === 200
    }

    return {
      showInfo,
      showError,
      showSuccess,
      showWarning,
      verifyExternalUrl,
      showFormattedError,
      formatGraphqlError,
      sendMessageToSentry: this.sendMessageToSentry
    }
  }
}
